import React, { useState } from "react";
import { IoPersonAddSharp } from "react-icons/io5";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Button from "../../components/ui/Button";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const AddStaffForm = ({ onAdd, onCancel }) => {
  const [form, setForm] = useState({
    name: "",
    subject: [""],
    className: [""],
    token: "",
    term: "first",
  });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [showToken, setShowToken] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (!form.name || !form.subject.filter(Boolean).length || !form.className.filter(Boolean).length || !form.token) {
      setErrorMsg("All fields are required.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/assign-teacher-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          className: form.className.filter(Boolean),
          subject: form.subject.filter(Boolean),
          token: form.token,
          term: form.term,
        }),
      });
      const result = await res.json();

      if (!res.ok || !result.success) {
        setErrorMsg(result.error || "Failed to add teacher.");
        setLoading(false);
        return;
      }

      setSuccessMsg("Teacher added successfully!");
      if (onAdd) onAdd(result.user);
      setForm({ name: "", subject: [""], className: [""], token: "", term: "first" });

      setTimeout(() => {
        setLoading(false);
        onCancel && onCancel();
      }, 1200);
    } catch (err) {
      setErrorMsg("Failed to add teacher.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 py-8">
      <div className="bg-white border border-primary/30 rounded-xl shadow-lg p-8 w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <IoPersonAddSharp size={28} className="text-primary" />
          <h2 className="text-2xl font-heading font-bold text-primary">Add New Teacher</h2>
        </div>
        {errorMsg && (
          <div className="mb-4 text-red-600 bg-red-50 border border-red-200 rounded p-2 text-center">
            {errorMsg}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 text-green-700 bg-green-50 border border-green-200 rounded p-2 text-center">
            {successMsg}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block font-semibold mb-1">Full Name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full border border-border rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter teacher's full name"
              required
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Subject(s)</label>
            {form.subject.map((subj, idx) => (
              <div key={idx} className="flex items-center mb-1">
                <input
                  type="text"
                  value={subj}
                  onChange={e => {
                    const newSubjects = [...form.subject];
                    newSubjects[idx] = e.target.value;
                    setForm(f => ({ ...f, subject: newSubjects }));
                  }}
                  className="w-full border rounded px-2 py-1"
                  placeholder={`Subject ${idx + 1}`}
                  required
                />
                {form.subject.length > 1 && (
                  <button
                    type="button"
                    className="ml-2 px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                    onClick={() => setForm(f => ({
                      ...f,
                      subject: f.subject.filter((_, i) => i !== idx)
                    }))}
                    title="Remove subject"
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="mt-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs"
              onClick={() => setForm(f => ({
                ...f,
                subject: [...f.subject, ""]
              }))}
            >
              + Add another subject
            </button>
          </div>
          <div>
            <label className="block font-semibold mb-1">Class(es)</label>
            {form.className.map((cls, idx) => (
              <div key={idx} className="flex items-center mb-1">
                <input
                  type="text"
                  value={cls}
                  onChange={e => {
                    const newClasses = [...form.className];
                    newClasses[idx] = e.target.value;
                    setForm(f => ({ ...f, className: newClasses }));
                  }}
                  className="w-full border rounded px-2 py-1"
                  placeholder={`Class ${idx + 1}`}
                  required
                />
                {form.className.length > 1 && (
                  <button
                    type="button"
                    className="ml-2 px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                    onClick={() => setForm(f => ({
                      ...f,
                      className: f.className.filter((_, i) => i !== idx)
                    }))}
                    title="Remove class"
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="mt-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs"
              onClick={() => setForm(f => ({
                ...f,
                className: [...f.className, ""]
              }))}
            >
              + Add another class
            </button>
          </div>
          <div>
            <label className="block font-semibold mb-1">Password / Token</label>
            <div className="relative">
              <input
                type={showToken ? "text" : "password"}
                name="token"
                value={form.token}
                onChange={handleChange}
                className="w-full border border-border rounded-lg py-2 px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-primary"
                required
                placeholder="Enter password/token"
              />
              <button
                type="button"
                className="absolute top-1/2 right-2 transform -translate-y-1/2 text-gray-500 hover:text-primary"
                onClick={() => setShowToken(v => !v)}
                tabIndex={-1}
                aria-label={showToken ? "Hide token" : "Show token"}
              >
                {showToken ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block font-semibold mb-1">Term</label>
            <select
              name="term"
              value={form.term}
              onChange={handleChange}
              className="w-full border border-border rounded-lg py-2 px-3"
              required
            >
              <option value="first">First Term</option>
              <option value="second">Second Term</option>
              <option value="third">Third Term</option>
            </select>
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? "Adding..." : "Add Teacher"}
          </Button>
        </form>
        <button
          className="mt-6 text-primary underline text-sm"
          onClick={() => onCancel && onCancel()}
        >
          &larr; Back
        </button>
      </div>
    </div>
  );
};

export default AddStaffForm;